import { v4 } from 'uuid';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { SummaryStatistics } from '~/domain/statistics/summaryStatistics';

/**
 * Smallest radius a circle may have, in mm. An edge click or resize closer to
 * the centre than this is treated as a slip and refused, so a circle can never
 * have zero area. It is below one screen pixel, so no circle the user can
 * actually draw is refused.
 */
export const MIN_CIRCLE_RADIUS_MM = 1e-3;

/**
 * A placed circle, with the statistics of the voxels inside it in the image's
 * native units. The circle stays in the plane it was placed on, and is always
 * read in that plane, whatever the view is turned to later. `stats` is null
 * when there is no image data under the circle; a zero is never invented.
 * The statistics are recomputed on every edit and cleared with the list, so
 * they never outlive the image they were read from.
 */
export interface CircleRegion extends AnnotationRecord {
    readonly centerMm: PointMm;
    readonly radiusMm: number;
    readonly stats: SummaryStatistics | null;
}

/** An in-progress placement; the radius follows the cursor until the second click. */
export interface CircleRegionDraft {
    readonly plane: AnnotationPlane;
    readonly centerMm: PointMm;
    readonly radiusMm: number;
}

/**
 * `circle`, placed or in progress, with its plane and centre moved by
 * `change`. The radius and the statistics are kept: a rigid move changes
 * neither.
 */
export function circleRegionInFrame<T extends CircleRegion | CircleRegionDraft>(
    circle: T,
    change: AnnotationFrameChange
): T {
    return {
        ...circle,
        plane: change.plane(circle.plane),
        centerMm: change.point(circle.centerMm),
    };
}

/** Collision-free id. `uuid`, not `crypto`, which is banned repo-wide. */
export function createCircleRegionId(): string {
    return `circle-${v4()}`;
}
